import type { Interpreter } from '../Interpreter';
import { isDevelopment } from '../util';

const machines = new Map<string, Interpreter>();
const registeredListeners: Array<
  (name: string, interpreter: Interpreter) => void
> = [];
const unregisteredListeners: Array<(name: string) => void> = [];
const lifecycleAttached = new WeakSet<Interpreter>();

function attachLifecycle(interpreter: Interpreter): void {
  if (lifecycleAttached.has(interpreter)) return;
  lifecycleAttached.add(interpreter);

  interpreter.onStart(() => {
    // A stop followed by a later start removes the interpreter from the map
    // before this callback runs, so register it again for DevTools.
    if (![...machines.values()].includes(interpreter)) {
      registerMachine(interpreter);
    }
  });

  interpreter.onStop(() => {
    void Promise.resolve().then(() => {
      const entry = [...machines.entries()].find(
        ([, registeredInterpreter]) => registeredInterpreter === interpreter
      );
      if (!interpreter.running && entry) {
        unregisterMachine(entry[0]);
      }
    });
  });
}

export function registerMachine(interpreter: Interpreter): void {
  if (!isDevelopment()) return;

  if ([...machines.values()].includes(interpreter)) return;

  const baseName = interpreter.machine.inspector.name;

  // If a stopped machine with this name already exists (HMR / React StrictMode
  // remount), replace it in-place so the panel sees one entry, not name + name#2.
  // The old interpreter's onStop handler checks `machines.get(name) === interpreter`
  // before unregistering, so it won't evict the new one.
  const existing = machines.get(baseName);
  if (existing && !existing.running) {
    machines.set(baseName, interpreter);
    attachLifecycle(interpreter);
    registeredListeners.forEach((cb) => cb(baseName, interpreter));
    return;
  }

  // Deduplicate: if a different running machine already uses this name, append #N.
  let name = baseName;
  let counter = 1;
  while (machines.has(name)) {
    name = `${baseName}#${counter}`;
    counter++;
  }

  machines.set(name, interpreter);
  attachLifecycle(interpreter);

  registeredListeners.forEach((cb) => cb(name, interpreter));
}

export function unregisterMachine(name: string): void {
  machines.delete(name);
  unregisteredListeners.forEach((cb) => cb(name));
}

export function getMachines(): ReadonlyMap<string, Interpreter> {
  return machines;
}

export function onMachineRegistered(
  cb: (name: string, interpreter: Interpreter) => void
): () => void {
  registeredListeners.push(cb);
  return () => {
    const i = registeredListeners.indexOf(cb);
    if (i !== -1) registeredListeners.splice(i, 1);
  };
}

export function onMachineUnregistered(cb: (name: string) => void): () => void {
  unregisteredListeners.push(cb);
  return () => {
    const i = unregisteredListeners.indexOf(cb);
    if (i !== -1) unregisteredListeners.splice(i, 1);
  };
}
